import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Bot, ExternalLink, Archive, FolderInput, Trash2, Share2 } from 'lucide-react';
import { AppShell } from '../components/AppShell';
import { Article, MediaChip } from '../components/ArticleCard';
import { MediaBody } from '../components/MediaBody';
import { ShareDialog } from '../components/ShareDialog';
import { apiFetch } from '../lib/api';
import { domainOf, titleOf } from '../lib/url';
import { useScrollProgress } from '../lib/useScrollProgress';
import { useReaderActions } from '../lib/useReaderActions';
import { useSaveFromLink } from '../lib/useSaveFromLink';

// ── Article list panel ────────────────────────────────────────────────────

function QueueList({
  articles,
  selectedId,
  onSelect,
}: {
  articles: Article[];
  selectedId: string | null;
  onSelect: (a: Article) => void;
}) {
  const reading = articles.filter(a => a.status === 'reading');
  const unread = articles.filter(a => a.status === 'unread');

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Panel header */}
      <div className="px-4 pt-5 pb-3 flex-shrink-0 border-b" style={{ borderColor: '#1E1E34' }}>
        <h1 className="font-bold text-base" style={{ color: '#EDEDF0' }}>Queue</h1>
        <p className="text-xs mt-0.5" style={{ color: '#5C5C72' }}>
          {reading.length > 0 ? `${reading.length} in progress · ` : ''}{unread.length} unread
        </p>
      </div>

      {/* Scrollable list */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-1.5">
        {/* Continue reading */}
        {reading.length > 0 && (
          <>
            <p className="text-xs font-semibold uppercase tracking-widest px-1 pb-1 pt-1" style={{ color: '#10B981' }}>
              Continue
            </p>
            {reading.map(a => (
              <QueueRow
                key={a.id}
                article={a}
                selected={selectedId === a.id}
                onClick={() => onSelect(a)}
              />
            ))}
            <div className="pt-1 pb-1" />
          </>
        )}

        {/* Up next */}
        {unread.length > 0 && (
          <>
            <p className="text-xs font-semibold uppercase tracking-widest px-1 pb-1" style={{ color: '#5C5C72' }}>
              Up next
            </p>
            {unread.map(a => (
              <QueueRow
                key={a.id}
                article={a}
                selected={selectedId === a.id}
                onClick={() => onSelect(a)}
              />
            ))}
          </>
        )}

        {articles.length === 0 && (
          <div className="py-12 px-2 text-center" style={{ color: '#5C5C72' }}>
            <p className="text-sm font-medium" style={{ color: '#C4C4D4' }}>Your queue is empty</p>
            <p className="text-xs mt-1.5 leading-relaxed">
              Save your first article with the browser extension or the mobile app.
            </p>
            <Link to="/welcome" className="text-xs mt-3 inline-flex items-center gap-1 text-violet-400 hover:text-violet-300 transition-colors">
              Set up Grove everywhere
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function QueueRow({ article: a, selected, onClick }: { article: Article; selected: boolean; onClick: () => void }) {
  const domain = domainOf(a.url);
  const mins = a.wordCount ? Math.max(1, Math.ceil(a.wordCount / 200)) : null;
  const progress = a.readingProgress ?? 0;

  return (
    <button
      onClick={onClick}
      className="w-full text-left rounded-xl px-3 py-3 transition-all"
      style={{
        background: selected ? 'rgba(139,92,246,0.12)' : '#0E0E18',
        border: `1px solid ${selected ? 'rgba(139,92,246,0.3)' : '#1E1E34'}`,
      }}
    >
      {/* Source + meta */}
      <div className="flex items-center gap-2 mb-1.5">
        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#8B5CF6' }} />
        <span className="text-xs flex-1 truncate" style={{ color: '#5C5C72' }}>{domain}</span>
        {a.addedBy && (
          <span className="flex-shrink-0" title={`Added by ${a.addedBy}`}>
            <Bot size={12} style={{ color: '#8B5CF6' }} />
          </span>
        )}
        {a.type === 'video' || a.type === 'pdf' ? (
          <span className="flex-shrink-0"><MediaChip article={a} /></span>
        ) : (
          mins && <span className="text-xs flex-shrink-0" style={{ color: '#3C3C54' }}>{mins} min</span>
        )}
      </div>

      {/* Title */}
      <p className="text-sm font-medium leading-snug mb-2 line-clamp-2" style={{
        color: selected ? '#EDEDF0' : '#C4C4D4',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
      }}>
        {titleOf(a)}
      </p>

      {/* Progress bar */}
      {(a.status === 'reading' || progress > 0) && (
        <div className="h-0.5 rounded-full overflow-hidden" style={{ background: '#1E1E34' }}>
          <div
            className="h-full"
            style={{
              width: `${progress}%`,
              background: 'linear-gradient(90deg, #8B5CF6, #06B6D4)',
              borderRadius: '0 1px 1px 0',
            }}
          />
        </div>
      )}
    </button>
  );
}

// ── Article metadata card ─────────────────────────────────────────────────

function ArticleMetaCard({ article: a, domain, mins }: { article: Article; domain: string; mins: number | null }) {
  const [editingTags, setEditingTags] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [tagInput, setTagInput] = useState((a.tags ?? []).join(', '));
  const [notesInput, setNotesInput] = useState(a.notes ?? '');

  const clippedDate = a.createdAt
    ? new Date(a.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  const saveField = async (field: 'tags' | 'notes', value: string | string[]) => {
    try {
      await apiFetch(`/articles/${a.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ [field]: value }),
      });
    } catch { /* best-effort */ }
  };

  return (
    <div className="rounded-xl mb-8 p-4" style={{ background: '#0E0E18', border: '1px solid #1E1E34' }}>
      {/* Row 1: domain · read time · clipped date */}
      <div className="flex flex-wrap items-center gap-3 mb-3 text-xs" style={{ color: '#6060780' }}>
        <a href={a.url} target="_blank" rel="noopener noreferrer" style={{ color: '#06B6D4', textDecoration: 'none' }} onMouseOver={e => (e.currentTarget.style.textDecoration='underline')} onMouseOut={e => (e.currentTarget.style.textDecoration='none')}>{domain}</a>
        {mins && <span style={{ color: '#5C5C72' }}>· {mins} min read</span>}
        {clippedDate && <span style={{ color: '#5C5C72' }}>· clipped {clippedDate}</span>}
        {a.addedBy && (
          <span className="px-2 py-0.5 rounded-md text-xs inline-flex items-center gap-1"
            style={{ background: 'rgba(139,92,246,0.10)', color: '#C4B5FD', border: '1px solid rgba(139,92,246,0.2)' }}>
            <Bot size={11} /> {a.addedBy}
          </span>
        )}
        {a.vaultPath && (
          <span className="px-2 py-0.5 rounded-md text-xs" style={{ background: 'rgba(6,182,212,0.10)', color: '#67E8F9', border: '1px solid rgba(6,182,212,0.2)' }}>
            {a.vaultPath}
          </span>
        )}
      </div>

      {/* Row 2: Tags */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {!editingTags ? (
          <>
            {(a.tags ?? []).length > 0
              ? (a.tags ?? []).map(t => (
                  <span key={t} className="px-2 py-0.5 rounded-md text-xs" style={{ background: 'rgba(139,92,246,0.10)', color: '#C4B5FD', border: '1px solid rgba(139,92,246,0.2)' }}>
                    #{t}
                  </span>
                ))
              : <span className="text-xs" style={{ color: '#3C3C54' }}>No tags</span>
            }
            <button onClick={() => setEditingTags(true)} className="text-xs px-2 py-0.5 rounded-md" style={{ color: '#5C5C72', border: '1px solid #2A2A44' }}>
              {(a.tags ?? []).length > 0 ? 'edit tags' : '+ add tags'}
            </button>
          </>
        ) : (
          <div className="flex items-center gap-2 w-full">
            <input
              autoFocus
              value={tagInput}
              onChange={e => setTagInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  const tags = tagInput.split(',').map(t => t.trim()).filter(Boolean);
                  saveField('tags', tags);
                  setEditingTags(false);
                  a.tags = tags;
                }
                if (e.key === 'Escape') setEditingTags(false);
              }}
              placeholder="ai, research, pkm"
              className="flex-1 text-xs px-3 py-1.5 rounded-lg outline-none"
              style={{ background: '#16162A', color: '#D4D4E0', border: '1px solid #2A2A44' }}
            />
            <button onClick={() => setEditingTags(false)} className="text-xs" style={{ color: '#5C5C72' }}>cancel</button>
          </div>
        )}
      </div>

      {/* Row 3: Notes */}
      <div>
        {!editingNotes ? (
          <div className="flex items-start gap-2">
            {notesInput
              ? <p className="text-xs flex-1" style={{ color: '#8888A0' }}>{notesInput}</p>
              : <span className="text-xs" style={{ color: '#3C3C54' }}>No notes</span>
            }
            <button onClick={() => setEditingNotes(true)} className="text-xs px-2 py-0.5 rounded-md flex-shrink-0" style={{ color: '#5C5C72', border: '1px solid #2A2A44' }}>
              {notesInput ? 'edit' : '+ add note'}
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <textarea
              autoFocus
              value={notesInput}
              onChange={e => setNotesInput(e.target.value)}
              rows={3}
              placeholder="Why did you save this?"
              className="w-full text-xs px-3 py-2 rounded-lg outline-none resize-none"
              style={{ background: '#16162A', color: '#D4D4E0', border: '1px solid #2A2A44' }}
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setEditingNotes(false)} className="text-xs" style={{ color: '#5C5C72' }}>cancel</button>
              <button
                onClick={() => { saveField('notes', notesInput); setEditingNotes(false); }}
                className="text-xs px-3 py-1 rounded-lg"
                style={{ background: 'rgba(139,92,246,0.15)', color: '#C4B5FD', border: '1px solid rgba(139,92,246,0.3)' }}
              >
                save
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Reader panel ──────────────────────────────────────────────────────────

function ReaderPanel({ article: a, onClose }: { article: Article; onClose: () => void }) {
  const domain = domainOf(a.url);
  // Video/pdf own their progress (watch point / page) — scroll restore and
  // writes are article-type only, and word count is transcript/description
  // noise for media, so no "min read" either.
  const isMedia = a.type === 'video' || a.type === 'pdf';
  const mins = !isMedia && a.wordCount ? Math.max(1, Math.ceil(a.wordCount / 200)) : null;
  const scrollRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const { liveProgress, onScroll, markDeleted } = useScrollProgress({
    articleId: a.id,
    savedProgress: a.readingProgress,
    status: a.status,
    contentReady: !isMedia && !!a.content,
    scrollRef,
  });

  const actions = useReaderActions(
    { id: a.id, url: a.url, title: titleOf(a) },
    { onDone: onClose, onBeforeDelete: markDeleted },
  );

  // Mark as reading on open (if still unread)
  useEffect(() => {
    if (a.status === 'unread') {
      apiFetch(`/articles/${a.id}`, { method: 'PATCH', body: JSON.stringify({ status: 'reading' }) })
        .then(() => queryClient.invalidateQueries({ queryKey: ['articles', 'queue'] }))
        .catch(() => {});
    }
  }, [a.id]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Reader top bar */}
      <div className="flex items-center justify-between px-6 py-3 border-b flex-shrink-0" style={{ borderColor: '#1E1E34', background: 'rgba(9,9,15,0.9)' }}>
        <button onClick={onClose} className="text-sm inline-flex items-center gap-1.5" style={{ color: '#8B5CF6' }}>
          <ArrowLeft size={14} /> Queue
        </button>
        <a href={a.url} target="_blank" rel="noopener" className="text-xs truncate max-w-xs" style={{ color: '#3C3C54' }}>{domain}</a>
        <a
          href={a.url}
          target="_blank"
          rel="noopener"
          className="text-xs px-3 py-1.5 rounded-lg font-semibold inline-flex items-center gap-1.5"
          style={{ background: 'rgba(139,92,246,0.12)', color: '#C4B5FD', border: '1px solid rgba(139,92,246,0.25)' }}
        >
          Open <ExternalLink size={12} />
        </a>
      </div>

      {/* Scrollable article content */}
      <div className="flex-1 overflow-y-auto" data-testid="reader-panel-scroll" ref={scrollRef} onScroll={isMedia ? undefined : onScroll}>
        <div className="max-w-2xl mx-auto px-8 py-8">

          {/* Title + byline */}
          <h1 className="text-3xl font-bold leading-tight mb-2" style={{ color: '#F0F0F8', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif', letterSpacing: '-0.02em' }}>
            {titleOf(a)}
          </h1>
          {a.author && (
            <p className="text-sm mb-4" style={{ color: '#8888A0' }}>by {a.author}</p>
          )}

          {/* Article metadata card */}
          <ArticleMetaCard article={a} domain={domain} mins={mins} />

          <MediaBody article={a} domain={domain} />
        </div>
      </div>

      {/* Bottom action bar */}
      <div className="flex-shrink-0" style={{ background: '#0E0E18', borderTop: '1px solid #1E1E34' }}>
        {/* Progress bar */}
        <div className="h-0.5" style={{ background: '#1E1E34' }}>
          <div style={{ width: `${liveProgress}%`, height: '100%', background: 'linear-gradient(90deg, #8B5CF6, #06B6D4)' }} />
        </div>
        {/* Actions — the four reader exits (Archive · File to… · Delete · Share) */}
        <div className="flex gap-2 px-4 py-3 justify-end">
          <button
            onClick={actions.archive}
            disabled={actions.isPending}
            className="px-4 py-1.5 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5"
            style={{ background: 'rgba(139,92,246,0.12)', color: '#C4B5FD', border: '1px solid rgba(139,92,246,0.2)' }}
          >
            <Archive size={14} /> Archive
          </button>
          <button
            onClick={actions.fileTo}
            disabled={actions.isPending}
            className="px-4 py-1.5 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5"
            style={{ background: 'rgba(6,182,212,0.10)', color: '#67E8F9', border: '1px solid rgba(6,182,212,0.2)' }}
          >
            <FolderInput size={14} /> File to…
          </button>
          <button
            onClick={actions.remove}
            disabled={actions.isPending}
            className="px-4 py-1.5 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5"
            style={{ background: 'rgba(92,92,114,0.1)', color: '#5C5C72', border: '1px solid rgba(92,92,114,0.2)' }}
          >
            <Trash2 size={14} /> Delete
          </button>
          <button
            onClick={actions.share}
            className="px-4 py-1.5 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5"
            style={{ background: 'rgba(92,92,114,0.1)', color: '#9898AC', border: '1px solid rgba(92,92,114,0.2)' }}
          >
            <Share2 size={14} /> Share
          </button>
        </div>
      </div>

      <ShareDialog
        article={{
          id: a.id,
          title: titleOf(a),
          author: a.author,
          siteName: a.siteName,
          image: a.image,
          wordCount: a.wordCount,
        }}
        open={actions.shareOpen}
        onClose={actions.closeShare}
      />
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────

/** Toast for saves that arrived via a share link's "Save to Grove" button. */
function SaveFromLinkToast() {
  const { status, dismiss } = useSaveFromLink();
  if (status === 'idle') return null;

  const copy: Record<Exclude<typeof status, 'idle'>, string> = {
    saving: 'Saving to your queue…',
    saved: 'Saved to your queue.',
    duplicate: 'That one’s already in your queue.',
    trial_expired: 'Your trial has ended — subscribe to keep saving.',
    error: 'Couldn’t save that article.',
  };

  return (
    <div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-lg text-sm text-white border border-white/10 shadow-lg"
      style={{ background: '#1A1A2E' }}
      role="status"
    >
      {copy[status]}
      {status === 'trial_expired' && (
        <Link to="/settings" className="ml-3 text-violet-400 font-medium hover:underline">
          Subscribe
        </Link>
      )}
      {status !== 'saving' && (
        <button onClick={dismiss} className="ml-3 text-text-secondary hover:text-white transition-colors">
          Dismiss
        </button>
      )}
    </div>
  );
}

export default function QueuePage() {
  const navigate = useNavigate();
  const { id: urlId } = useParams<{ id?: string }>();

  const { data: articles = [] } = useQuery<Article[]>({
    queryKey: ['articles', 'queue'],
    queryFn: () => apiFetch('/articles?status=unread,reading'),
  });

  // Full article fetch when URL has an ID
  const { data: selectedArticle } = useQuery<Article>({
    queryKey: ['article', urlId],
    queryFn: () => apiFetch(`/articles/${urlId}`),
    enabled: !!urlId,
  });

  // Optimistic: show stub from list while full article loads
  const listArticle = urlId ? articles.find(a => a.id === urlId) : undefined;
  const displayArticle = selectedArticle ?? listArticle ?? null;

  return (
    <>
    <SaveFromLinkToast />
    <AppShell
      list={
        <QueueList
          articles={articles}
          selectedId={urlId ?? null}
          onSelect={a => navigate(`/queue/${a.id}`)}
        />
      }
      panel={displayArticle ? (
        <ReaderPanel
          article={displayArticle}
          onClose={() => navigate('/queue', { replace: true })}
        />
      ) : undefined}
      showPanel={!!urlId}
    />
    </>
  );
}
